import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ElementRef,
    Input,
    OnDestroy,
    ViewContainerRef,
} from '@angular/core';
import {MatButton} from '@angular/material/button';
import {OverlayPanelRef} from '@common/core/ui/overlay-panel/overlay-panel-ref';
import {UpgradeOverlayComponent} from '../upgrade-overlay/upgrade-overlay.component';
import {OverlayPanel} from '@common/core/ui/overlay-panel/overlay-panel.service';
import {Settings} from '@common/core/config/settings.service';
import {CurrentUser} from '@common/auth/current-user';
import {BOTTOM_POSITION} from '@common/core/ui/overlay-panel/positions/bottom-position';
import {SimplePermissionOverlayComponent} from '../simple-permission-overlay/simple-permission-overlay.component';
import {ComponentType} from '@angular/cdk/portal';
import {
    CreateMsgType,
    LinkFeaturePermissions,
    LinkUsageResponse,
} from '../../../shared/link/link-usage-response';
import {LinkUsageService} from '../../../shared/link/link-usage.service';
import {MatMenuItem} from '@angular/material/menu';
import {Subscription} from 'rxjs';

export interface PermissionBtnConfig {
    hasPermission: boolean;
    action: Action;
    resource: string;
    createMsgType?: CreateMsgType;
    userOwnsWorkspace: boolean;
    forWorkspace: boolean;
}

type Action = 'create' | 'edit' | 'delete';

@Component({
    selector: '[permission-aware-button], permission-aware-button',
    templateUrl: './permission-aware-button.component.html',
    styleUrls: ['./permission-aware-button.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermissionAwareButtonComponent
    implements AfterViewInit, OnDestroy
{
    @Input() permission: string;
    @Input() restriction: keyof LinkFeaturePermissions;
    @Input() userOwnsResource: boolean;
    @ContentChild(MatButton) matButton: MatButton;
    @ContentChild(MatMenuItem) matMenuItem: MatMenuItem;

    config: PermissionBtnConfig;
    private overlayRef: OverlayPanelRef;
    private overlayCmp: ComponentType<any>;
    private regularButton: HTMLButtonElement;
    private usageSub: Subscription;

    constructor(
        private cd: ChangeDetectorRef,
        private overlay: OverlayPanel,
        private viewContainerRef: ViewContainerRef,
        private settings: Settings,
        private currentUser: CurrentUser,
        private el: ElementRef<HTMLElement>,
        private linkUsage: LinkUsageService
    ) {}

    ngAfterViewInit() {
        setTimeout(() => {
            if (!this.matButton && !this.matMenuItem) {
                this.regularButton = this.el.nativeElement.querySelector('button');
            }
            this.usageSub = this.linkUsage.response$.subscribe(response => {
                this.hydrateConfig(response);
                if (this.config.hasPermission || this.userOwnsResource) {
                    this.toggleButtonState(false);
                    this.overlayCmp = null;
                } else {
                    this.toggleButtonState(true);
                    this.setOverlayCmp();
                }
                this.cd.markForCheck();
            });
            this.createHoverListener();
        });
    }

    ngOnDestroy() {
        this.usageSub?.unsubscribe();
    }

    private hydrateConfig(response: LinkUsageResponse) {
        const [resource, action] = this.permission.split('.');
        this.config = {
            resource,
            action: action as Action,
            hasPermission:
                response.usage[resource][this.restriction || action] ||
                this.currentUser.isAdmin(),
            forWorkspace: response.forWorkspace,
            createMsgType: response.usage[resource].createMsgType || this.restriction,
            userOwnsWorkspace: response.userOwnsWorkspace,
        };
    }

    private setOverlayCmp() {
        if (this.shouldShowUpgradeOverlay()) {
            this.overlayCmp = UpgradeOverlayComponent;
        } else {
            this.overlayCmp = SimplePermissionOverlayComponent;
        }
    }

    private shouldShowUpgradeOverlay(): boolean {
        return (
            this.settings.get('billing.enable') &&
            this.config.action === 'create' &&
            !this.config.hasPermission &&
            this.config.createMsgType !== 'noWorkspacePermission'
        );
    }

    private createHoverListener() {
        this.el.nativeElement.addEventListener('mouseenter', () => {
            if (this.overlayCmp) {
                this.cd.markForCheck();
                this.showMessage();
            }
        });
        this.el.nativeElement.addEventListener('mouseleave', e => {
            const toElement = e.relatedTarget as HTMLElement;
            if (
                this.overlayRef &&
                !toElement.classList.contains('permission-overlay')
            ) {
                this.hideMessage();
            }
        });
    }

    private toggleButtonState(disabled: boolean) {
        const btn = this.matButton || this.matMenuItem || this.regularButton;
        btn.disabled = disabled;
    }

    showMessage() {
        if (this.overlayRef) {
            this.hideMessage();
        }
        this.overlayRef = this.overlay.open(this.overlayCmp, {
            origin: this.matButton._elementRef,
            position: BOTTOM_POSITION,
            hasBackdrop: false,
            scrollStrategy: 'close',
            viewContainerRef: this.viewContainerRef,
            data: {config: this.config},
        });
        this.overlayRef.getPanelEl().addEventListener('mouseleave', () => {
            this.hideMessage();
        });
    }

    hideMessage() {
        if (this.overlayRef) {
            this.overlayRef.close();
            this.overlayRef = null;
        }
    }
}
